import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import { FilterAutocomplete, Table } from "../../../../components/ui";
import usePagination from "../../../../hooks/usePagination";
import { TCP_STEPS_COLUMNS } from "../../constants";
import { complianceSearchActions } from "../../../../reducers/complianceSearch";
import { useComplianceSearchContext } from "../../../../context";
import { COMPLIANCE_API_URLS, ComplianceAPI } from "../../../../api/compliance";
import { appendCommaSeparatedValuesToQuery, TitleUpdated } from "../../utils";
import { SectionTitle } from "../../styledComponents";

const TCPStepsSearch: React.FC = () => {
  const dispatch = useDispatch();
  const pagination = usePagination();

  const {
    data: { results, updated },
    loading,
  } = useSelector((state) => state.complianceSearch.tcpSteps);

  const { tabsTablesSearchState, setTabsTablesSearchState } = useComplianceSearchContext();
  const { names, compliance_entries_id } =
    tabsTablesSearchState.fundedAccountQuickSearchTab.tcpStepsSearchTable.search;

  const { isError, error } = useQuery({
    enabled: names?.length >= 2 || compliance_entries_id?.length > 0,
    queryKey: [
      COMPLIANCE_API_URLS.getTCPSteps,
      names,
      compliance_entries_id,
      pagination.page,
      pagination.limit,
    ],
    async queryFn({ signal }) {
      dispatch(complianceSearchActions.fetchTCPStepsRequest());

      const query = new URLSearchParams();
      query.append("limit", pagination.limit.toString());
      query.append("page", pagination.page.toString());

      names && appendCommaSeparatedValuesToQuery(names, query, "names");
      compliance_entries_id &&
        appendCommaSeparatedValuesToQuery(compliance_entries_id, query, "compliance_entries_id");

      const response = await ComplianceAPI.getTCPStepsRequest(query.toString(), signal);
      pagination.setCount(response.data.results.count);
      dispatch(complianceSearchActions.fetchTCPStepsSuccess(response.data));
      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      console.error("Error fetching TCP steps", error);
      dispatch(complianceSearchActions.fetchTCPStepsFailed());
    }
  }, [isError, error]);

  return (
    <SectionTitle title="TCP Steps">
      <FilterAutocomplete
        search={{
          compliance_entries_id,
          names,
        }}
        setSearch={(values) => {
          setTabsTablesSearchState({
            ...tabsTablesSearchState,
            fundedAccountQuickSearchTab: {
              ...tabsTablesSearchState.fundedAccountQuickSearchTab,
              tcpStepsSearchTable: {
                search: {
                  names: values.names,
                  compliance_entries_id: values.compliance_entries_id,
                },
              },
            },
          });
        }}
        filters={{
          EntriesId: "compliance_entries_id",
          Name: "names",
        }}
      />

      <Table
        columns={TCP_STEPS_COLUMNS}
        data={results.rows}
        loading={loading}
        pagination={pagination}
        extractKey={(row) => row.id}
        title={<TitleUpdated value={updated?.timestamp} />}
        csv_table_name="Funded account quick search - TCP Steps"
      />
    </SectionTitle>
  );
};

export default TCPStepsSearch;
